import { DateTime } from "luxon";

import { ValidationResultLevel } from "../common/enums";
import { MINIMUM_TIME_BEFORE_UPDATE_SECONDS } from "../constants/accountConstants";
import {
  TRANSFER_ON_DEATH_BENEFICIARY_FORM_SCHEMA,
  TRANSFER_ON_DEATH_BENEFICIARY_SCHEMA,
} from "../validations";
import { DateOnly } from "../date_utils/DateOnly";

export type UpdateClearingHouseAccountArgs = {
  clearingAccountCreationTime: Date;
  isEditAllowed: boolean;
};

export enum AccountUpdateValidationResult {
  InsufficientTimeElapsedSinceCreation = "InsufficientTimeElapsedSinceCreation",
  NotAllowed = "NotAllowed",
}

export const AccountUpdateValidationResultLevelMap: Record<
  AccountUpdateValidationResult,
  ValidationResultLevel
> = {
  InsufficientTimeElapsedSinceCreation: ValidationResultLevel.Error,
  NotAllowed: ValidationResultLevel.Error,
};

export const validateAccountUpdate = (args: UpdateClearingHouseAccountArgs) => {
  if (!args.isEditAllowed) return AccountUpdateValidationResult.NotAllowed;

  const diff = DateTime.now().diff(
    DateTime.fromJSDate(args.clearingAccountCreationTime),
  );
  if (diff.milliseconds / 1000 < MINIMUM_TIME_BEFORE_UPDATE_SECONDS) {
    return AccountUpdateValidationResult.InsufficientTimeElapsedSinceCreation;
  }
};

type UpdateClearingHouseAccountFormsArgs = {
  clearingAccountCreationTime: Date;
  isEditAllowed: boolean;
  forms: {
    transferOnDeath?: TransferOnDeathForm;
  };
};

export enum AccountFormsUpdateValidationResult {
  InsufficientTimeElapsedSinceCreation = "InsufficientTimeElapsedSinceCreation",
  NotAllowed = "NotAllowed",
  NoFormsToUpdate = "NoFormsToUpdate",
  InvalidForms = "InvalidForms",
}

export const AccountUpdateFormsValidationResultLevelMap: Record<
  AccountFormsUpdateValidationResult,
  ValidationResultLevel
> = {
  InsufficientTimeElapsedSinceCreation: ValidationResultLevel.Error,
  NotAllowed: ValidationResultLevel.Error,
  NoFormsToUpdate: ValidationResultLevel.Error,
  InvalidForms: ValidationResultLevel.Error,
};

export const validateAccountFormsUpdate = (
  args: UpdateClearingHouseAccountFormsArgs,
) => {
  if (!args.isEditAllowed) return AccountFormsUpdateValidationResult.NotAllowed;

  const diff = DateTime.now().diff(
    DateTime.fromJSDate(args.clearingAccountCreationTime),
  );
  if (diff.milliseconds / 1000 < MINIMUM_TIME_BEFORE_UPDATE_SECONDS) {
    return AccountFormsUpdateValidationResult.InsufficientTimeElapsedSinceCreation;
  }

  if (Object.keys(args.forms).length === 0) {
    return AccountFormsUpdateValidationResult.NoFormsToUpdate;
  }

  if (args.forms.transferOnDeath) {
    return validateTransferOnDeathForm(args.forms.transferOnDeath);
  }
};

type TransferOnDeathBeneficiary = Omit<
  TRANSFER_ON_DEATH_BENEFICIARY_SCHEMA,
  "dateOfBirthOrCreation" | "taxId"
> & {
  dateOfBirthOrCreation: DateOnly;
  taxId?: string;
};

type TransferOnDeathForm = {
  primaryBeneficiaries: TransferOnDeathBeneficiary[];
  contingentBeneficiaries?: TransferOnDeathBeneficiary[];
  spouseName?: string;
  jointSignature?: boolean;
  spouseSignature?: boolean;
};

const validateTransferOnDeathForm = (form: TransferOnDeathForm) => {
  // Validate using the schema
  const result = TRANSFER_ON_DEATH_BENEFICIARY_FORM_SCHEMA.safeParse({
    ...form,
    primaryBeneficiaries: form.primaryBeneficiaries.map((beneficiary) => ({
      ...beneficiary,
      dateOfBirthOrCreation:
        beneficiary.dateOfBirthOrCreation.toFormat("yyyy-MM-dd"),
    })),
    contingentBeneficiaries: form.contingentBeneficiaries?.map(
      (beneficiary) => ({
        ...beneficiary,
        dateOfBirthOrCreation:
          beneficiary.dateOfBirthOrCreation.toFormat("yyyy-MM-dd"),
      }),
    ),
  });
  if (!result.success) {
    return AccountFormsUpdateValidationResult.InvalidForms;
  }

  // If spouse name is provided, spouse signature is required, and vice versa
  if (
    (form.spouseName && !form.spouseSignature) ||
    (!form.spouseName && form.spouseSignature)
  ) {
    return AccountFormsUpdateValidationResult.InvalidForms;
  }
};
